import type { InitialServiceProfessional } from "../core/appointmentInitialServiceProfessionals";
import { type AppointmentResourceOnlyExecutionV1 } from "@/app/booking/core/appointmentResourceOnlyExecutionV1";
import {
  addGroupParticipant,
  applyAppointmentDraftServiceSequences,
  createIndividualAppointmentDraft,
  switchAppointmentDraftToGroup,
  type AppointmentBookingDraftV1,
  type AppointmentBundleItemSelectionDraft,
  type AppointmentDraftMode,
  type AppointmentEntryPoint,
  type AppointmentOnlineBookingConstraints,
  type AppointmentServiceLineDraft,
  type AppointmentStaffAssignmentPolicySnapshot,
  type AppointmentStaffPreference,
  type AppointmentTimeSegmentDraft,
} from "@/app/booking/core/appointmentBookingDraft";
import type {
  SpecialistAssignment,
  SpecialistService,
  SpecialistServiceOffering,
  SpecialistServiceSelection,
  SpecialistWorkplace,
} from "@/app/specialists/model";
import {
  getSpecialistGroupBookingPolicy,
  resolveSpecialistOnlineBookingPolicy,
} from "@/app/specialists/onlineBooking";
import { specialistPriceAmountToMinorUnits } from "@/app/specialists/priceSemantics";
import { resolveSpecialistServiceConfiguration } from "@/app/specialists/serviceAddOns";
import {
  createSpecialistServiceBundleAddOnPlan,
  resolveSpecialistServiceBundleConfiguration,
} from "@/app/specialists/serviceBundleAddOns";
import { normalizeSpecialistServiceTimeSegments } from "@/app/specialists/serviceTimeSegments";
import { getSpecialistServiceSale } from "@/app/specialists/servicePromotions";
import { getSelectableSpecialistServiceVariant } from "@/app/specialists/serviceVariants";

export type AppointmentServicesDraftFailureReason =
  | "catalog-version-mismatch"
  | "group-booking-unavailable"
  | "invalid-catalog"
  | "invalid-draft"
  | "invalid-selection"
  | "mixed-currency"
  | "not-online-bookable";

export type CreateAppointmentServicesDraftResult =
  | {
      ok: true;
      draft: AppointmentBookingDraftV1;
    }
  | {
      ok: false;
      reason: AppointmentServicesDraftFailureReason;
    };

export interface AppointmentServicesDraftGroupGuestInput {
  participantId: string;
  selections: readonly SpecialistServiceSelection[];
}

export type AppointmentServicesDraftOrigin =
  | {
      kind: "specialist-services";
      assignment: SpecialistAssignment;
    }
  | {
      kind: "location-services";
      catalogAssignment: SpecialistAssignment;
      locationId: string;
      resourceOnlyExecutionsV1?: readonly AppointmentResourceOnlyExecutionV1[];
      initialProfessionalUserLocationId?: string;
      initialServiceProfessionals?: readonly InitialServiceProfessional[];
    };

export interface CreateAppointmentServicesDraftInput {
  draftId: string;
  mode: AppointmentDraftMode;
  origin: AppointmentServicesDraftOrigin;
  workplace: SpecialistWorkplace;
  services: readonly SpecialistService[];
  offerings: readonly SpecialistServiceOffering[];
  selections: readonly SpecialistServiceSelection[];
  groupGuests?: readonly AppointmentServicesDraftGroupGuestInput[];
}

function snapshotOnlineBookingConstraints(
  constraints: AppointmentOnlineBookingConstraints,
) {
  return {
    audiences: [...constraints.audiences],
    bookableWeekdays: [...constraints.bookableWeekdays],
    manualConfirmation: constraints.manualConfirmation,
    requiredResourceIds: [...constraints.requiredResourceIds],
    availabilityPolicyId: constraints.availabilityPolicyId,
    catalogVersion: constraints.catalogVersion,
    ...(constraints.resourceOnlyExecutionV1 ? { resourceOnlyExecutionV1: constraints.resourceOnlyExecutionV1 } : {}),
  } satisfies AppointmentOnlineBookingConstraints;
}

function getResolvedOnlineBookingConstraints(
  assignment: SpecialistAssignment,
  workplace: SpecialistWorkplace,
  offering: SpecialistServiceOffering,
  origin: AppointmentServicesDraftOrigin,
) {
  const policy = resolveSpecialistOnlineBookingPolicy(assignment, offering, origin.kind === "location-services" ? origin.resourceOnlyExecutionsV1 : undefined);

  if (!policy.isBookable) {
    return { ok: false as const, reason: "not-online-bookable" as const };
  }

  const workplaceCatalogVersion = workplace.catalogVersion.trim();

  if (
    !workplaceCatalogVersion ||
    policy.constraints.catalogVersion !== workplaceCatalogVersion
  ) {
    return {
      ok: false as const,
      reason: "catalog-version-mismatch" as const,
    };
  }

  return {
    ok: true as const,
    staffAutoAssignmentEligible: policy.isAutoAssignable,
    constraints: snapshotOnlineBookingConstraints(policy.constraints),
  };
}

function getStaffAssignmentPolicySnapshot(
  workplace: SpecialistWorkplace,
): AppointmentStaffAssignmentPolicySnapshot | null {
  const policy: unknown = workplace.appointmentAssignmentPolicy;

  if (
    typeof policy !== "object" ||
    policy === null ||
    !("policyId" in policy) ||
    typeof policy.policyId !== "string" ||
    !policy.policyId.trim() ||
    !("allowAnyProfessional" in policy) ||
    typeof policy.allowAnyProfessional !== "boolean" ||
    !("allowSplitAcrossProfessionals" in policy) ||
    typeof policy.allowSplitAcrossProfessionals !== "boolean"
  ) {
    return null;
  }

  return {
    policyId: policy.policyId.trim(),
    allowAnyProfessional: policy.allowAnyProfessional,
    allowSplitAcrossProfessionals: policy.allowSplitAcrossProfessionals,
  };
}

function resolveAppointmentTimeSegments(
  offering: SpecialistServiceOffering,
  durationMinutes: number,
  durationMaxMinutes: number | null,
): readonly AppointmentTimeSegmentDraft[] | null {
  const template = normalizeSpecialistServiceTimeSegments(
    offering.timeSegments,
  );

  if (
    !template ||
    !Number.isSafeInteger(durationMinutes) ||
    durationMinutes < 1 ||
    (durationMaxMinutes !== null &&
      (!Number.isSafeInteger(durationMaxMinutes) ||
        durationMaxMinutes < durationMinutes))
  ) {
    return null;
  }

  return template.map((segment, order): AppointmentTimeSegmentDraft => {
    if (segment.kind === "service") {
      const usesResolvedDuration = segment.durationMinutes === null;

      return {
        segmentId: `${segment.kind}-${order + 1}`,
        kind: segment.kind,
        order,
        durationMinutes: segment.durationMinutes ?? durationMinutes,
        durationMaxMinutes: usesResolvedDuration
          ? durationMaxMinutes
          : null,
      };
    }

    return {
      segmentId: `${segment.kind}-${order + 1}`,
      kind: segment.kind,
      order,
      durationMinutes: segment.durationMinutes,
      durationMaxMinutes: null,
    };
  });
}

function getCatalogAssignment(origin: AppointmentServicesDraftOrigin) {
  return origin.kind === "specialist-services"
    ? origin.assignment
    : origin.catalogAssignment;
}

function createEntryPoint(
  origin: AppointmentServicesDraftOrigin,
  locationId: string,
): AppointmentEntryPoint {
  return origin.kind === "specialist-services"
    ? {
        kind: "specialist-services",
        locationId,
        userLocationId: origin.assignment.userLocationId,
      }
    : {
        kind: "location-services",
        locationId,
        ...(origin.initialServiceProfessionals?.length
          ? { initialServiceProfessionals: origin.initialServiceProfessionals.map(row => ({ ...row })) }
          : {}),
        ...(origin.initialProfessionalUserLocationId
          ? {
              initialProfessionalUserLocationId:
                origin.initialProfessionalUserLocationId,
            }
          : {}),
      };
}

function createInitialStaffPreference(
  origin: AppointmentServicesDraftOrigin,
): AppointmentStaffPreference {
  return origin.kind === "specialist-services"
    ? {
        kind: "specific",
        userLocationId: origin.assignment.userLocationId,
        source: "specialist-entry",
      }
    : { kind: "unresolved" };
}

function createInitialBundleItemStaffPreference(input: {
  bundleItemIndex: number;
  origin: AppointmentServicesDraftOrigin;
  scheduleType: "parallel" | "sequential";
}): AppointmentStaffPreference {
  if (
    input.origin.kind === "specialist-services" &&
    input.scheduleType === "parallel" &&
    input.bundleItemIndex > 0
  ) {
    return { kind: "unresolved" };
  }

  return createInitialStaffPreference(input.origin);
}

function createUnassignedLocationGuestStaffPreference(
  autoAssignmentEligible: boolean,
  policy: AppointmentStaffAssignmentPolicySnapshot,
): AppointmentStaffPreference {
  return policy.allowAnyProfessional && autoAssignmentEligible
    ? { kind: "any" }
    : { kind: "unresolved" };
}

function normalizeLocationGroupGuestLines(
  lines: readonly AppointmentServiceLineDraft[],
  policy: AppointmentStaffAssignmentPolicySnapshot,
) {
  return lines.map((line): AppointmentServiceLineDraft => ({
    ...line,
    selection:
      line.selection.kind === "bundle"
        ? {
            ...line.selection,
            bundleItems: line.selection.bundleItems.map((item) => ({
              ...item,
              staffPreference: createUnassignedLocationGuestStaffPreference(
                item.staffAutoAssignmentEligible,
                policy,
              ),
            })),
          }
        : line.selection,
    staffPreference: createUnassignedLocationGuestStaffPreference(
      line.staffAutoAssignmentEligible,
      policy,
    ),
  }));
}

function getMinorQuote(
  price: { amount: number; currency: string; type: "fixed" | "from" | "free" },
  durationMinutes: number,
  durationMaxMinutes: number | null,
) {
  const currency = price.currency.trim().toUpperCase();
  const amountMinor = specialistPriceAmountToMinorUnits(
    price.amount,
    currency,
  );

  if (amountMinor === null) {
    return null;
  }

  return {
    amountMinor,
    currency,
    priceType: price.type,
    durationMinutes,
    durationMaxMinutes,
  };
}

/**
 * Snapshots compare-at evidence only for the exact configuration where Step 1
 * presents it: the provider-authored base offering without a selected variant
 * or add-ons. The former price is never consulted by totals or checkout.
 */
function getMinorCompareAtQuote(input: {
  configuredPrice: {
    amount: number;
    currency: string;
    type: "fixed" | "from" | "free";
  };
  offering: SpecialistServiceOffering;
  presentationEligible: boolean;
}) {
  if (!input.presentationEligible) {
    return null;
  }

  const sale = getSpecialistServiceSale(input.offering);
  const chargedCurrency = input.configuredPrice.currency.trim().toUpperCase();
  const offeringPrice = input.offering.price;

  if (
    !sale ||
    !offeringPrice ||
    offeringPrice.amount !== input.configuredPrice.amount ||
    offeringPrice.currency.trim().toUpperCase() !== chargedCurrency ||
    offeringPrice.type !== input.configuredPrice.type ||
    sale.compareAtPrice.type === "free"
  ) {
    return null;
  }

  const chargedAmountMinor = specialistPriceAmountToMinorUnits(
    input.configuredPrice.amount,
    chargedCurrency,
  );
  const compareAtAmountMinor = specialistPriceAmountToMinorUnits(
    sale.compareAtPrice.amount,
    sale.compareAtPrice.currency,
  );

  if (
    chargedAmountMinor === null ||
    compareAtAmountMinor === null ||
    compareAtAmountMinor <= chargedAmountMinor
  ) {
    return null;
  }

  return {
    amountMinor: compareAtAmountMinor,
    currency: chargedCurrency,
    priceType: input.configuredPrice.type,
  };
}

function createStandaloneLine(input: {
  origin: AppointmentServicesDraftOrigin;
  assignment: SpecialistAssignment;
  workplace: SpecialistWorkplace;
  service: SpecialistService;
  offering: SpecialistServiceOffering;
  selection: SpecialistServiceSelection;
  selectionOrder: number;
}):
  | { ok: true; line: AppointmentServiceLineDraft }
  | {
      ok: false;
      reason:
        | "catalog-version-mismatch"
        | "invalid-selection"
        | "not-online-bookable";
    } {
  const {
    origin,
    assignment,
    workplace,
    service,
    offering,
    selection,
    selectionOrder,
  } = input;
  const onlineBooking = getResolvedOnlineBookingConstraints(
    assignment,
    workplace,
    offering,
    origin,
  );

  if (!onlineBooking.ok) {
    return onlineBooking;
  }

  const configuration = resolveSpecialistServiceConfiguration(
    offering,
    selection,
  );

  if (!configuration) {
    return { ok: false, reason: "invalid-selection" };
  }

  const quote = getMinorQuote(
    configuration.price,
    configuration.durationMinutes,
    configuration.durationMaxMinutes,
  );

  if (!quote) {
    return { ok: false, reason: "invalid-selection" };
  }

  const timeSegments = resolveAppointmentTimeSegments(
    offering,
    configuration.durationMinutes,
    configuration.durationMaxMinutes,
  );

  if (!timeSegments) {
    return { ok: false, reason: "invalid-selection" };
  }

  const variant = selection.variantId
    ? getSelectableSpecialistServiceVariant(offering, selection.variantId)
    : null;
  const compareAt = getMinorCompareAtQuote({
    configuredPrice: configuration.price,
    offering,
    presentationEligible:
      variant === null && configuration.addOnLines.length === 0,
  });

  return {
    ok: true,
    line: {
      lineId: `line-${selectionOrder + 1}`,
      selectionOrder,
      scheduleOrder: selectionOrder,
      selection: {
        kind: "service",
        serviceId: service.serviceId,
        offeringId: offering.offeringId,
        variantId: configuration.selection.variantId,
        addOns: (configuration.selection.addOns ?? []).map((addOn) => ({
          ...addOn,
        })),
        timeSegments,
      },
      staffPreference: onlineBooking.constraints.resourceOnlyExecutionV1 ? { kind: "not-required" } : createInitialStaffPreference(origin),
      staffAutoAssignmentEligible:
        onlineBooking.staffAutoAssignmentEligible,
      onlineBooking: onlineBooking.constraints,
      quote: {
        ...quote,
        ...(compareAt ? { compareAt } : {}),
      },
      snapshot: {
        serviceName: service.name,
        variantName: variant?.name ?? null,
        addOns: configuration.addOnLines.map(
          ({ group, addOn, quantity }) => ({
            bundleItemId: null,
            groupId: group.groupId,
            addOnId: addOn.addOnId,
            name: addOn.name,
            quantity,
          }),
        ),
      },
    },
  };
}

function createBundleLine(input: {
  origin: AppointmentServicesDraftOrigin;
  assignment: SpecialistAssignment;
  workplace: SpecialistWorkplace;
  service: SpecialistService;
  offering: SpecialistServiceOffering;
  selection: SpecialistServiceSelection;
  selectionOrder: number;
  serviceById: ReadonlyMap<string, SpecialistService>;
  offeringByServiceId: ReadonlyMap<string, SpecialistServiceOffering>;
}):
  | { ok: true; line: AppointmentServiceLineDraft }
  | {
      ok: false;
      reason:
        | "catalog-version-mismatch"
        | "invalid-catalog"
        | "invalid-selection"
        | "not-online-bookable";
    } {
  const {
    origin,
    assignment,
    workplace,
    service,
    offering,
    selection,
    selectionOrder,
    serviceById,
    offeringByServiceId,
  } = input;

  if (service.kind !== "bundle") {
    return { ok: false, reason: "invalid-catalog" };
  }

  const onlineBooking = getResolvedOnlineBookingConstraints(
    assignment,
    workplace,
    offering,
    origin,
  );

  if (!onlineBooking.ok) {
    return onlineBooking;
  }

  const plan = createSpecialistServiceBundleAddOnPlan({
    parentService: service,
    parentOffering: offering,
    resolveChild: (serviceId) => {
      const childService = serviceById.get(serviceId);
      const childOffering = offeringByServiceId.get(serviceId);

      return childService && childOffering
        ? { service: childService, offering: childOffering }
        : null;
    },
  });
  const configuration = plan
    ? resolveSpecialistServiceBundleConfiguration(plan, selection)
    : null;

  if (!plan) {
    return { ok: false, reason: "invalid-catalog" };
  }

  if (!configuration?.canConfirm) {
    return { ok: false, reason: "invalid-selection" };
  }

  const quote = getMinorQuote(
    configuration.price,
    configuration.durationMinutes,
    configuration.durationMaxMinutes,
  );

  if (!quote) {
    return { ok: false, reason: "invalid-selection" };
  }
  const compareAt = getMinorCompareAtQuote({
    configuredPrice: configuration.price,
    offering,
    presentationEligible: configuration.addOnLines.length === 0,
  });

  const configuredItemById = new Map(
    configuration.bundleItems.map((item) => [
      item.bundleItem.itemId,
      item,
    ]),
  );
  const bundleItems: AppointmentBundleItemSelectionDraft[] = [];

  for (const [bundleItemIndex, item] of plan.items.entries()) {
    if (
      !assignment.serviceIds.includes(item.service.serviceId) ||
      item.offering.userLocationId !== assignment.userLocationId
    ) {
      return { ok: false, reason: "invalid-catalog" };
    }

    const childOnlineBooking = getResolvedOnlineBookingConstraints(
      assignment,
      workplace,
      item.offering,
      origin,
    );

    if (!childOnlineBooking.ok) {
      return childOnlineBooking;
    }

    const configuredItem = configuredItemById.get(item.bundleItem.itemId);
    const bundleItemDuration = item.bundleItem.durationMinutes;
    const bundleItemDurationMax = item.bundleItem.durationMaxMinutes;
    const addedDurationMinutes =
      configuredItem?.addOnLines.reduce(
        (total, addOnLine) => total + addOnLine.addedDurationMinutes,
        0,
      ) ?? 0;

    if (
      bundleItemDuration === null ||
      !Number.isSafeInteger(addedDurationMinutes)
    ) {
      return { ok: false, reason: "invalid-catalog" };
    }

    const durationMinutes = bundleItemDuration + addedDurationMinutes;
    const effectiveDurationMaxMinutes =
      (bundleItemDurationMax ?? bundleItemDuration) +
      addedDurationMinutes;
    const durationMaxMinutes =
      effectiveDurationMaxMinutes === durationMinutes
        ? null
        : effectiveDurationMaxMinutes;
    const timeSegments = resolveAppointmentTimeSegments(
      item.offering,
      durationMinutes,
      durationMaxMinutes,
    );

    if (!timeSegments) {
      return { ok: false, reason: "invalid-catalog" };
    }

    bundleItems.push({
      bundleItemId: item.bundleItem.itemId,
      serviceId: item.service.serviceId,
      offeringId: item.offering.offeringId,
      variantId: item.variantId,
      addOns: (configuredItem?.selection.addOns ?? []).map((addOn) => ({
        ...addOn,
      })),
      staffPreference: childOnlineBooking.constraints.resourceOnlyExecutionV1 ? { kind: "not-required" } : createInitialBundleItemStaffPreference({
        bundleItemIndex,
        origin,
        scheduleType: service.scheduleType,
      }),
      staffAutoAssignmentEligible:
        childOnlineBooking.staffAutoAssignmentEligible,
      timeSegments,
      onlineBooking: childOnlineBooking.constraints,
    });
  }

  return {
    ok: true,
    line: {
      lineId: `line-${selectionOrder + 1}`,
      selectionOrder,
      scheduleOrder: selectionOrder,
      selection: {
        kind: "bundle",
        serviceId: service.serviceId,
        offeringId: offering.offeringId,
        scheduleType: service.scheduleType,
        resourceAllocation: service.resourceAllocation,
        bundleItems,
      },
      staffPreference: onlineBooking.constraints.resourceOnlyExecutionV1 ? { kind: "not-required" } : createInitialStaffPreference(origin),
      staffAutoAssignmentEligible:
        onlineBooking.staffAutoAssignmentEligible,
      onlineBooking: onlineBooking.constraints,
      quote: {
        ...quote,
        ...(compareAt ? { compareAt } : {}),
      },
      snapshot: {
        serviceName: service.name,
        variantName: null,
        addOns: configuration.addOnLines.map(
          ({ bundleItem, group, addOn, quantity }) => ({
            bundleItemId: bundleItem.itemId,
            groupId: group.groupId,
            addOnId: addOn.addOnId,
            name: addOn.name,
            quantity,
          }),
        ),
      },
    },
  };
}

function createParticipantLines(input: {
  origin: AppointmentServicesDraftOrigin;
  assignment: SpecialistAssignment;
  workplace: SpecialistWorkplace;
  selections: readonly SpecialistServiceSelection[];
  serviceById: ReadonlyMap<string, SpecialistService>;
  offeringByServiceId: ReadonlyMap<string, SpecialistServiceOffering>;
}):
  | { ok: true; lines: readonly AppointmentServiceLineDraft[] }
  | {
      ok: false;
      reason: AppointmentServicesDraftFailureReason;
    } {
  const {
    origin,
    assignment,
    workplace,
    selections,
    serviceById,
    offeringByServiceId,
  } = input;

  if (
    selections.length === 0 ||
    new Set(selections.map(({ serviceId }) => serviceId)).size !==
      selections.length
  ) {
    return { ok: false, reason: "invalid-selection" };
  }

  const lines: AppointmentServiceLineDraft[] = [];

  for (const [selectionOrder, selection] of selections.entries()) {
    const service = serviceById.get(selection.serviceId);
    const offering = offeringByServiceId.get(selection.serviceId);

    if (
      !service ||
      !offering ||
      offering.userLocationId !== assignment.userLocationId ||
      !assignment.serviceIds.includes(selection.serviceId)
    ) {
      return { ok: false, reason: "invalid-catalog" };
    }

    const result =
      service.kind === "bundle"
        ? createBundleLine({
            origin,
            assignment,
            workplace,
            service,
            offering,
            selection,
            selectionOrder,
            serviceById,
            offeringByServiceId,
          })
        : createStandaloneLine({
            origin,
            assignment,
            workplace,
            service,
            offering,
            selection,
            selectionOrder,
          });

    if (!result.ok) {
      return result;
    }

    lines.push(result.line);
  }

  return { ok: true, lines };
}

/**
 * Route-independent Step-1 service adapter. It rebuilds configured catalog
 * rows atomically and differs by origin only at the entry-point and initial
 * staff boundary. Location tasks start unresolved; Specialist tasks remain
 * fixed to the exact entry assignment.
 */
export function createAppointmentServicesDraft({
  draftId,
  mode,
  origin,
  workplace,
  services,
  offerings,
  selections,
  groupGuests = [],
}: CreateAppointmentServicesDraftInput): CreateAppointmentServicesDraftResult {
  const assignment = getCatalogAssignment(origin);
  const locationId =
    origin.kind === "location-services"
      ? origin.locationId
      : assignment.locationId;

  if (
    !draftId.trim() ||
    !locationId.trim() ||
    workplace.locationId !== locationId ||
    assignment.locationId !== locationId ||
    (origin.kind === "specialist-services" &&
      !assignment.isPublicProfileVisible) ||
    selections.length === 0 ||
    (mode === "individual" && groupGuests.length > 0)
  ) {
    return { ok: false, reason: "invalid-catalog" };
  }

  const serviceById = new Map(
    services.map((service) => [service.serviceId, service]),
  );
  const offeringByServiceId = new Map(
    offerings.map((offering) => [offering.serviceId, offering]),
  );
  const offeringIds = offerings.map(({ offeringId }) => offeringId.trim());

  if (
    serviceById.size !== services.length ||
    offeringByServiceId.size !== offerings.length ||
    offeringIds.some((offeringId) => !offeringId) ||
    new Set(offeringIds).size !== offeringIds.length ||
    new Set(groupGuests.map(({ participantId }) => participantId)).size !==
      groupGuests.length ||
    groupGuests.some(
      ({ participantId, selections: guestSelections }) =>
        !participantId.trim() || guestSelections.length === 0,
    )
  ) {
    return { ok: false, reason: "invalid-catalog" };
  }

  const organizerLines = createParticipantLines({
    origin,
    assignment,
    workplace,
    selections,
    serviceById,
    offeringByServiceId,
  });

  if (!organizerLines.ok) {
    return organizerLines;
  }

  const resolvedGuests: Array<{
    participantId: string;
    lines: readonly AppointmentServiceLineDraft[];
  }> = [];

  for (const guest of groupGuests) {
    const result = createParticipantLines({
      origin,
      assignment,
      workplace,
      selections: guest.selections,
      serviceById,
      offeringByServiceId,
    });

    if (!result.ok) {
      return result;
    }

    resolvedGuests.push({
      participantId: guest.participantId,
      lines: result.lines,
    });
  }

  const currencies = new Set(
    [organizerLines.lines, ...resolvedGuests.map(({ lines }) => lines)]
      .flat()
      .map(({ quote }) => quote.currency.trim().toUpperCase()),
  );

  if (currencies.size !== 1) {
    return { ok: false, reason: "mixed-currency" };
  }

  const staffAssignmentPolicy = getStaffAssignmentPolicySnapshot(workplace);

  if (!staffAssignmentPolicy) {
    return { ok: false, reason: "invalid-catalog" };
  }

  let draft = createIndividualAppointmentDraft({
    draftId,
    locationId,
    timeZone: workplace.timeZone,
    entryPoint: createEntryPoint(origin, locationId),
    staffAssignmentPolicy,
    organizerLines: organizerLines.lines,
  });

  if (!draft) {
    return { ok: false, reason: "invalid-draft" };
  }

  if (mode === "group") {
    const groupPolicy = getSpecialistGroupBookingPolicy(workplace);

    if (!groupPolicy) {
      return { ok: false, reason: "group-booking-unavailable" };
    }

    draft = switchAppointmentDraftToGroup(draft, groupPolicy);

    if (!draft) {
      return { ok: false, reason: "invalid-draft" };
    }

    for (const guest of resolvedGuests) {
      const guestLines =
        origin.kind === "location-services"
          ? normalizeLocationGroupGuestLines(
              guest.lines,
              staffAssignmentPolicy,
            )
          : guest.lines;
      draft = addGroupParticipant(draft, {
        participantId: guest.participantId,
        role: "guest",
        identity: null,
        serviceLines: guestLines,
      });

      if (!draft) {
        return { ok: false, reason: "invalid-draft" };
      }
    }
  }

  const sequencedDraft = applyAppointmentDraftServiceSequences(
    draft,
    workplace.serviceBookingSequences,
  );

  return sequencedDraft
    ? { ok: true, draft: sequencedDraft }
    : { ok: false, reason: "invalid-draft" };
}
